import _ from 'lodash';
import React, {memo, useCallback, useMemo, useRef} from 'react';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';

import {Table} from '../../../../ui-toolkit/elements/tableV2/Table.tsx';
import {Icon, Spacer} from '../../../../ui-toolkit/index';
import {NotificationCard} from '../../../../ui-toolkit/indexExportV2';

import {getPathDetails, unwrapNullableDataType} from '../../../../ducks/formattersettingshelpers';
import {usePreferences} from '../../../../ducks/PreferencesContext';
import {useFormattersByIds} from '../../../FormattersContext';
import EntityTableRow from './EntityTableRow';
import {FilterRow, FilterRowCell} from './FilterRow.jsx';
import {FooterRow} from './FooterRow';
import {useQuickFilters} from './quick-filters/QuickFiltersContext';
import {buildQuickFilter, canUseQuickFilter} from './quick-filters/utils';
import {SkeletonRow} from './SkeletonRow';
import TableTooltip from './TableTooltip';

function EntityTable({
    entityType: dataType,
    formatter,
    entitiesState,
    entities,
    entityClick,
    entityPeek,
    currentSort,
    setSort,
    loadMore,
    markedRows,
    showContextMenu,
    filterFormatters,
    filterOnField,
    t,
}) {
    const {preferences} = usePreferences();
    const shownColumns = _.get(preferences, 'shownColumns', []);

    const entityType = unwrapNullableDataType(dataType);

    // determine whether the entities are being loaded; uses the legacy `loading` and
    // tries to improve the UX when the new `isLoadingMore` is present
    // = true for initial load, load more, filter/sort change
    const entitiesLoading = Boolean(entitiesState.loading || entitiesState.isLoadingMore);

    // determine whether the entities are being reloaded; prefers the new `isReloading` (from useEntityList)
    // and falls back to the legacy `loading` to match the legacy UX
    // = true for initial load, user clicks reload
    const entitiesReloading = Boolean(entitiesState.isReloading ?? entitiesState.loading);

    const formatterAttributes = useMemo(() => {
        return _.filter(
            _.get(formatter, 'settings.attributes', []),
            (attr) => getPathDetails(entityType, attr.attributePath).validPath,
        );
    }, [entityType]);

    const thClick = useCallback(
        (fieldName) => {
            setSort({
                field: fieldName,
                dir: _.get(currentSort, 'field') === fieldName ? !currentSort.dir : true,
            });
        },
        [setSort, currentSort],
    );

    const includedAttributes = useMemo(
        () =>
            _.isEmpty(shownColumns)
                ? formatterAttributes
                : _.filter(formatterAttributes, (attr) => _.includes(shownColumns, attr.uniqueId)),
        [shownColumns, formatterAttributes],
    );

    const colFormatterIds = useMemo(() => _.map(includedAttributes, 'formatterId'), [includedAttributes]);

    const quickFilters = useQuickFilters();

    const formatters = useFormattersByIds(colFormatterIds);

    /**
     * A map of column unique IDs to booleans indicating whether the column can have a quick filter applied.
     * @type {Object<number, boolean>}
     */
    const isQuickFilterEnabledByColumn = useMemo(() => {
        return _.reduce(
            includedAttributes,
            (agg, attribute) => ({
                ...agg,
                [attribute.uniqueId]:
                    quickFilters.isEnabled &&
                    canUseQuickFilter(attribute, _.find(formatters, {id: attribute.formatterId}), entityType),
            }),
            {},
        );
    }, [entityType, formatters, includedAttributes, quickFilters.isEnabled]);

    const handleQuickSearch = useCallback(
        /**
         * Handles an update to the quick filter query of a given column.
         *
         * @param {string} query The search query entered by the user.
         * @param {object} column The column that the quick filter search query applies to.
         * @returns {void}
         */
        (query, column) => {
            if (_.isEmpty(query)) {
                quickFilters.unset(column.uniqueId);
                return;
            }

            const formatter = _.find(formatters, {id: column.formatterId});
            const filter = buildQuickFilter(query, column, formatter, entityType);
            if (_.isEmpty(filter)) return;

            quickFilters.set(column.uniqueId, filter);
        },
        [entityType, formatters, quickFilters],
    );

    const columnInfo = useMemo(
        () =>
            includedAttributes.map((attr, ix) => {
                const pathDetails = getPathDetails(entityType, attr.attributePath);
                return {
                    pathDetails: pathDetails,
                    attrFormatter: _.find(formatters, {id: attr.formatterId}),

                    filterFormatter: _.find(filterFormatters, (x) => _.isEqual(x.attributePath, attr.attributePath)),

                    isQuickFilterEnabled: isQuickFilterEnabledByColumn[attr.uniqueId],
                    quickFilter: (query) => handleQuickSearch(query, attr),

                    sort: _.get(currentSort, 'field') === pathDetails.pathName ? currentSort.dir : null,
                    onSortClick: () => thClick(pathDetails.pathName),

                    displayName: attr.displayName,
                    columnId: ix,
                };
            }),
        [
            entityType,
            includedAttributes,
            formatters,
            filterFormatters,
            isQuickFilterEnabledByColumn,
            currentSort,
            thClick,
            handleQuickSearch,
        ],
    );

    const rowInfo = useMemo(
        () =>
            _.map(entities, (ent, ix) => {
                return {
                    rowId: ix,

                    entity: ent,
                    entityId: ent.entityId,

                    rowClick: (ev) =>
                        entityClick ? entityClick(entityType.dataTypeName, ent.entityId, ev) : undefined,
                    rowPeek: () => (entityPeek ? entityPeek(entityType.dataTypeId, ent) : undefined),

                    columns: columnInfo.map((attr) => {
                        const value = _.get(ent, attr.pathDetails.pathName);
                        return {
                            columnInfo: attr,
                            value: value,
                            filterOnField:
                                attr.filterFormatter == null
                                    ? null
                                    : () => filterOnField(attr.filterFormatter.attributePath, value),
                        };
                    }),

                    isMarked: _.includes(markedRows, ent.entityId),
                };
            }),
        [entities, entityClick, entityPeek, filterOnField, columnInfo, entityType, markedRows],
    );

    // We need a ref to the tbody because we listen to right-click events on it, for the context menu
    const tbodyRef = useRef();

    return (
        <Table.StickyScroll>
            <thead>
                <Table.Row>
                    {columnInfo.map((attr) => (
                        <Table.HeaderSortable
                            isCurrentSort={attr.sort != null}
                            currentDirection={attr.sort}
                            key={attr.columnId}
                            onClick={attr.onSortClick}>
                            {attr.displayName}
                        </Table.HeaderSortable>
                    ))}
                    <Table.Header />
                </Table.Row>

                {quickFilters.isEnabled && _.some(columnInfo, 'isQuickFilterEnabled') && (
                    <FilterRow>
                        {columnInfo.map((attribute) =>
                            attribute.isQuickFilterEnabled ? (
                                <FilterRowCell key={attribute.columnId} onChange={attribute.quickFilter} />
                            ) : (
                                <Table.Header key={attribute.columnId} />
                            ),
                        )}
                        {/* This empty <Table.Header> is needed because there is an extra column with actions that doesn't have a header. */}
                        <Table.Header />
                    </FilterRow>
                )}
            </thead>
            <tbody ref={tbodyRef}>
                {/* Reloading, no entities available -> single skeleton row */}
                {entitiesReloading && _.isEmpty(rowInfo) && <SkeletonRow />}

                {/* Reloading, some entities available -> skeleton row per entity to indicate they are reloading */}
                {entitiesReloading && _.some(rowInfo) && rowInfo.map((ent) => <SkeletonRow key={ent.entityId} />)}

                {/* Not reloading, some entities available -> render them */}
                {!entitiesReloading &&
                    _.some(rowInfo) &&
                    rowInfo.map((row) => (
                        <EntityTableRow
                            key={row.rowId}
                            row={row}
                            entityType={entityType}
                            showContextMenu={showContextMenu}
                            t={t}
                        />
                    ))}

                {/* Loading initial/more data -> show a single row below the available entities (if any) */}
                {entitiesLoading && <SkeletonRow />}

                {/* No attributes have been set yet, show an error */}
                {_.isEmpty(includedAttributes) && (
                    <tr>
                        <td colSpan={999}>
                            <NotificationCard color="danger">
                                <Spacer.Align space="xsm">
                                    <Icon name="warning" />
                                    {t('entityTable.noColumns')}
                                </Spacer.Align>
                            </NotificationCard>
                        </td>
                    </tr>
                )}

                {/* Not loading initial data -> show empty message */}
                {!entitiesLoading && _.isEmpty(entities) && !_.isEmpty(includedAttributes) && (
                    <Table.Row>
                        <EmptyTableData colSpan={999}>{t('entityTable.noResults')} </EmptyTableData>
                    </Table.Row>
                )}

                {_.defaultTo(showContextMenu, true) && (
                    <TableTooltip outerRef={tbodyRef} rows={rowInfo} entityType={entityType} t={t} />
                )}
            </tbody>
            <FooterRow entitiesState={entitiesState} loadMore={loadMore} />
        </Table.StickyScroll>
    );
}

export default withTranslation()(memo(EntityTable));

const EmptyTableData = styled(Table.Data)`
    height: 34px;
`;
